// FichePaieDetail.jsx
import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faDownload, faPrint, faSpinner } from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import logo from '../../../assetes/logos.png';

const API_URL = `${API_CONFIG2.apiUrl}employe/api_paie.php`;

const FichePaieDetail = ({ ficheId, onClose }) => {
  const [fiche, setFiche] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const payslipRef = useRef(null);

  useEffect(() => {
    const loadFiche = async () => {
      try {
        const res = await fetch(`${API_URL}?action=get_fiche&id=${ficheId}`);
        const data = await res.json();
        if (data.status === 'success') {
          setFiche(data.fiche);
        } else {
          setError(data.message || 'Erreur chargement détail');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadFiche();
  }, [ficheId]);

  const handleCapture = async () => {
    if (!payslipRef.current) return;
    setCapturing(true);
    try {
      const canvas = await html2canvas(payslipRef.current, {
        scale: 2,
        backgroundColor: '#ffffff', // fond blanc pour la capture
        allowTaint: false,
        useCORS: true,
        logging: false,
      });
      const link = document.createElement('a');
      link.download = `fiche_paie_${fiche.periode}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Erreur capture:', err);
      alert('Erreur lors de la capture de la fiche.');
    } finally {
      setCapturing(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div style={{ textAlign: 'center', padding: '2rem', color: '#e0e7ff' }}>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <p>Chargement du détail...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <p style={{ color: '#ef4444' }}>Erreur : {error}</p>
          <button className="btn-payroll" onClick={onClose}>Fermer</button>
        </div>
      </div>
    );
  }

  if (!fiche) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content glass" onClick={(e) => e.stopPropagation()}>
        {/* En-tête du modal avec actions */}
        <div className="modal-header">
          <h3 style={{ color: '#e0e7ff' }}>Détail de la fiche de paie</h3>
          <div>
            <button className="btn-payroll" onClick={handleCapture} disabled={capturing}>
              {capturing ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faDownload} />}
              {capturing ? ' Capture...' : ' PNG'}
            </button>
            <button className="btn-payroll" onClick={handlePrint}>
              <FontAwesomeIcon icon={faPrint} /> Imprimer
            </button>
            <button className="btn-payroll" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Zone à capturer : fond blanc, style classique */}
        <div ref={payslipRef} className="payslip-container">
          {/* En-tête avec logo */}
          <div className="payslip-header">
            <img src={logo} alt="Logo" className="payslip-logo" />
            <div className="payslip-title">
              <h2>FICHE DE PAIE</h2>
              <p>Période : {fiche.periode}</p>
            </div>
          </div>

          {/* Informations employé */}
          <div className="payslip-info">
            <div><strong>Employé :</strong> {fiche.matricule} - {fiche.nom} {fiche.prenom}</div>
            <div><strong>Statut :</strong> <span className={`badge-status ${fiche.statut}`}>{fiche.statut}</span></div>
            {fiche.date_paiement && (
              <div><strong>Payé le :</strong> {fiche.date_paiement}</div>
            )}
            <div><strong>Salaire net :</strong> {parseFloat(fiche.salaire_net).toFixed(2)} USD</div>
          </div>

          {/* Lignes de paie */}
          <h4 className="payslip-subtitle">Détail des lignes</h4>
          <table className="payroll-table">
            <thead>
              <tr>
                <th>Libellé</th>
                <th style={{ textAlign: 'right' }}>Montant</th>
              </tr>
            </thead>
            <tbody>
              {fiche.lignes && fiche.lignes.length > 0 ? (
                fiche.lignes.map((ligne, idx) => (
                  <tr key={idx}>
                    <td>{ligne.libelle}</td>
                    <td style={{ textAlign: 'right' }}>{parseFloat(ligne.montant).toFixed(2)} USD</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="2">Aucune ligne</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td><strong>Total net à payer</strong></td>
                <td style={{ textAlign: 'right' }}><strong>{parseFloat(fiche.salaire_net).toFixed(2)} USD</strong></td>
              </tr>
            </tfoot>
          </table>
          <div className="payslip-footer">
            Généré le {new Date().toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Styles CSS */}
      <style>{`
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 10, 30, 0.85);
          backdrop-filter: blur(4px);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 1rem;
        }
        .modal-content {
          max-width: 850px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          border-radius: 20px;
          padding: 1.5rem;
          background: rgba(10, 20, 50, 0.7);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.15);
          box-shadow: 0 8px 32px rgba(0,0,0,0.6);
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .modal-header h3 {
          margin: 0;
          color: #e0e7ff;
        }

        /* Boutons style sombre */
        .btn-payroll {
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.15);
          color: #e0e7ff;
          padding: 0.4rem 1.2rem;
          border-radius: 30px;
          cursor: pointer;
          transition: 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
        }
        .btn-payroll:hover {
          background: rgba(255,255,255,0.15);
        }
        .btn-payroll:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* ---------- Zone de la fiche (fond blanc) ---------- */
        .payslip-container {
          background: #ffffff;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          color: #1a1a2e;
        }
        .payslip-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          border-bottom: 2px solid #2d3748;
          padding-bottom: 1rem;
        }
        .payslip-logo {
          height: 60px;
          object-fit: contain;
        }
        .payslip-title {
          text-align: right;
        }
        .payslip-title h2 {
          margin: 0;
          color: #1a365d;
          font-size: 1.6rem;
          letter-spacing: 1px;
        }
        .payslip-title p {
          margin: 0;
          color: #4a5568;
          font-size: 0.9rem;
        }

        .payslip-info {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.8rem 1.5rem;
          margin-bottom: 1.5rem;
          padding: 0.8rem 0;
          border-bottom: 1px solid #e2e8f0;
        }
        .payslip-info div {
          font-size: 0.95rem;
        }
        .payslip-info strong {
          color: #2d3748;
        }

        .payslip-subtitle {
          font-size: 1.1rem;
          color: #2d3748;
          margin: 1rem 0 0.5rem 0;
        }

        .payroll-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.95rem;
          color: #1a1a2e;
        }
        .payroll-table th {
          text-align: left;
          font-size: 0.75rem;
          text-transform: uppercase;
          color: #4a5568;
          padding: 0.6rem 0.4rem;
          border-bottom: 2px solid #e2e8f0;
          background: #f7fafc;
        }
        .payroll-table td {
          padding: 0.5rem 0.4rem;
          border-bottom: 1px solid #edf2f7;
        }
        .payroll-table tr:hover {
          background: #f7fafc;
        }
        .payroll-table .total-row {
          border-top: 2px solid #2d3748;
          font-weight: 600;
        }
        .payroll-table .total-row td {
          padding-top: 0.8rem;
        }

        .payslip-footer {
          margin-top: 1.5rem;
          text-align: right;
          font-size: 0.8rem;
          color: #718096;
          border-top: 1px solid #e2e8f0;
          padding-top: 0.8rem;
        }

        /* Badges de statut (adaptés fond blanc) */
        .badge-status {
          padding: 0.2rem 0.8rem;
          border-radius: 30px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-block;
        }
        .badge-status.Brouillon {
          background: #fef3c7;
          color: #92400e;
        }
        .badge-status.Validé {
          background: #dbeafe;
          color: #1e40af;
        }
        .badge-status.Payé {
          background: #d1fae5;
          color: #065f46;
        }
        .badge-status.Demandé {
          background: #fef3c7;
          color: #92400e;
        }
        .badge-status.Approuvé {
          background: #dbeafe;
          color: #1e40af;
        }
        .badge-status.Remboursé {
          background: #d1fae5;
          color: #065f46;
        }
        .badge-status.Annulé {
          background: #fee2e2;
          color: #991b1b;
        }

        /* Impression */
        @media print {
          .modal-overlay {
            position: static;
            background: none;
            backdrop-filter: none;
            padding: 0;
          }
          .modal-content {
            background: none !important;
            backdrop-filter: none !important;
            border: none !important;
            box-shadow: none !important;
            padding: 0 !important;
            max-height: none;
            overflow: visible;
          }
          .modal-header {
            display: none !important;
          }
          .payslip-container {
            border-radius: 0;
            box-shadow: none;
            padding: 1rem;
          }
          .btn-payroll {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default FichePaieDetail;